import React, { useEffect, useState } from 'react';
import { collabApi } from '../utils/collab';
import { formatStamp } from '../utils/datetime';
import '../styles/CollabSettings.css';

/**
 * Admin → Settings → Collaboration. Written by an Orcanos administrator, read by everyone
 * (Critical Note #20).
 *
 * ⚠️ This switch is ON by default and an absent row reads as ON — the opposite of the BOM
 * module's opt-in licence, and deliberately so. Collaboration is a property of the app
 * given to every account at once, so an absent value means "nobody has turned it off".
 * The screen says as much, because an admin looking at a fresh tenant needs to know
 * whether they are seeing a setting or a default.
 */
export default function CollabSettingsForm() {
  const [values, setValues] = useState({
    enabled: true, allow_follow_me: true, retention_days: 90,
  });
  const [meta, setMeta] = useState({ updated_at: '', updated_by: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    collabApi.settings()
      .then((data) => {
        if (!alive) return;
        setValues({
          enabled: data.enabled !== false,
          allow_follow_me: data.allow_follow_me !== false,
          retention_days: data.retention_days ?? 90,
        });
        setMeta({ updated_at: data.updated_at || '', updated_by: data.updated_by || '' });
      })
      .catch((err) => { if (alive) setError(err.message || 'Could not read these settings.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const set = (patch) => { setValues((v) => ({ ...v, ...patch })); setSaved(false); };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const data = await collabApi.saveSettings({
        enabled: values.enabled,
        allow_follow_me: values.allow_follow_me,
        retention_days: Number(values.retention_days) || 0,
      });
      setMeta({ updated_at: data.updated_at || '', updated_by: data.updated_by || '' });
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save these settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="collab-settings-loading">Loading collaboration settings…</div>;

  return (
    <div className="collab-settings">
      <div className="collab-settings-head">
        <h2>Collaboration</h2>
        {!meta.updated_at && <span className="collab-settings-badge">On by default</span>}
      </div>
      <p className="collab-settings-blurb">
        Presence and chat for everyone signed in to this account. Turning it off hides the
        rail for all users; existing history is retained, not deleted.
      </p>

      <div className="collab-settings-choices">
        {[
          { on: true, name: 'On', hint: 'Colleagues see who is online and can chat.' },
          { on: false, name: 'Off', hint: 'No presence, no chat, anywhere in the app.' },
        ].map((opt) => (
          <button
            key={String(opt.on)}
            type="button"
            className={`collab-pref-option${values.enabled === opt.on ? ' collab-pref-option--on' : ''}`}
            onClick={() => set({ enabled: opt.on })}
            aria-pressed={values.enabled === opt.on}
          >
            <span className="collab-pref-radio" aria-hidden="true" />
            <span className="collab-pref-text">
              <span className="collab-pref-name">{opt.name}</span>
              <span className="collab-pref-hint">{opt.hint}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="collab-settings-row">
        <input
          id="collab-allow-follow"
          type="checkbox"
          checked={values.allow_follow_me}
          onChange={(e) => set({ allow_follow_me: e.target.checked })}
        />
        <label htmlFor="collab-allow-follow">
          Allow &ldquo;Follow me&rdquo;
          <span className="collab-settings-hint">
            A colleague can mirror someone&rsquo;s view for a short time. Nothing is
            captured — they see their own rendering of the same panel, under their own
            permissions.
          </span>
        </label>
      </div>

      <div className="collab-settings-row collab-settings-row--inline">
        <label htmlFor="collab-retention">Message retention</label>
        <input
          id="collab-retention"
          type="number"
          min="0"
          max="3650"
          value={values.retention_days}
          onChange={(e) => set({ retention_days: e.target.value })}
        />
        <span>days</span>
        <span className="collab-settings-note">0 = keep forever</span>
      </div>
      <p className="collab-settings-blurb">
        Messages are hard-deleted on this schedule, and users are told the number in the chat
        panel footer. Chat is the one thing in this product Orcanos cannot rebuild — it is
        backup-critical, and it is personal data.
      </p>

      {error && <div className="collab-refusal" role="alert">{error}</div>}

      <div className="collab-settings-actions">
        <button type="button" className="collab-settings-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="collab-settings-ok">Saved.</span>}
        {meta.updated_at && (
          <span className="collab-settings-note">
            Last changed{meta.updated_by ? ` by ${meta.updated_by}` : ''} · {formatStamp(meta.updated_at)}
          </span>
        )}
      </div>
    </div>
  );
}
